import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { PolymorphicComponentPropWithRef, PolymorphicRef } from '../types';
import { onRoleButtonKeyDown, toReactProps } from '../utils';
import { ScreenReaderOnly } from '../';
import titleStyles from '../title/title.module.css';
import styles from './progress.module.css';
import clsx from 'clsx';
import '@u-elements/u-progress';

type Size = 'sm' | 'lg';
type Value = string | number;
type Variants = 'bar' | 'dot' | 'step-vertical' | 'step-horizontal';

export type ProgressProps<
  Variant extends Variants,
  As extends React.ElementType = Variant extends 'bar' ? 'progress' : 'div'
> = PolymorphicComponentPropWithRef<
  As,
  { 'aria-label': string; value?: Value } & (
    | {
        variant: 'bar';
        size?: never;
        max: Value;
        justify?: never;
      }
    | {
        variant: 'dot';
        size?: Size;
        max?: Value;
        justify?: never;
      }
    | {
        variant: 'step-vertical';
        size?: Size;
        max?: never;
        justify?: never;
      }
    | {
        variant: 'step-horizontal';
        size?: Size;
        max?: never;
        justify?: 'center' | 'space-between';
      }
  )
>;

type ProgressComponent<
  Variant extends Variants = 'bar',
  As extends React.ElementType = Variant extends 'bar' ? 'progress' : 'div'
> = (props: ProgressProps<Variant, As>) => JSX.Element;

const ProgressComp: ProgressComponent = forwardRef(function Progress<
  Variant extends Variants,
  As extends React.ElementType = Variant extends 'bar' ? 'progress' : 'div'
>(
  {
    children,
    className,
    value,
    max,
    justify,
    size,
    variant = 'bar',
    ...rest
  }: ProgressProps<Variant, As>,
  ref: PolymorphicRef<As>
) {
  const intMax = Number(max) || Number(value) || 0;
  const intValue = Math.max(0, Math.min(Number(value) || 0, intMax)); // Ensure value is minimum 0 and maximum intMax
  const innerRef = useRef<As>(null);
  const isStep = variant.startsWith('step-');
  const role = isStep ? 'list' : 'group';
  const classes = clsx(
    styles[variant],
    styles[size || 'lg'],
    !justify || styles[justify],
    className
  );

  useImperativeHandle(ref, () => innerRef.current as As); // Forward innerRef
  useEffect(() => {
    const self = innerRef.current as HTMLElement | null;
    const steps = self?.querySelectorAll<HTMLElement>(`.${styles.step}`);
    const titles = self?.querySelectorAll<HTMLElement>(
      `.${styles.step} .${titleStyles.title}:first-child`
    );

    /* Setup steps */
    titles?.forEach((title) => (title.role = 'presentation')); // Make title not announce as part of step
    steps?.forEach((step, index) => {
      const desc = step.querySelector(`.${styles.description}`)?.textContent;

      if (!desc && !step.ariaLabel)
        console.warn(self, `must have aria-label or children.`);

      step.ariaCurrent = `${index === intValue && 'step'}`; // Set state in client with vanilla JS for performance
      step.classList.toggle(styles.isNext, index > intValue);
      step.classList.toggle(styles.isCurrent, index === intValue);
      step.classList.toggle(styles.isPrev, index < intValue);
      step.style.animationDelay = `${index * 200}ms`;
    });

    /* Setup progress between steps */
    const decimal = intValue - Math.floor(intValue);
    const stepA = steps?.[Math.floor(intValue)];
    const stepB = steps?.[Math.ceil(intValue)];
    let resizeObserver: ResizeObserver;

    // ResizeObserver is not supported in Vitest
    if (decimal && stepA && stepB && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => {
        const rectA = getRect(stepA?.querySelector(`.${styles.dot}`));
        const rectB = getRect(stepB?.querySelector(`.${styles.dot}`));

        stepA?.style.setProperty(
          '--progress-between',
          `${Math.round((variant === 'step-vertical' ? rectB.top - rectA.bottom : rectB.left - rectA.right) * decimal)}px`
        );
      });
      resizeObserver.observe(stepA);
      resizeObserver.observe(stepB);
    }

    return () => {
      titles?.forEach((title) => title.removeAttribute('role'));
      stepA?.style.removeProperty('--progress-between');
      resizeObserver?.disconnect();
    };
  }, [intValue, variant]);

  return variant === 'bar' ? (
    <u-progress
      class={classes}
      max={intMax}
      ref={innerRef}
      value={intValue} // Limit to maximum intMax
      {...rest}
    />
  ) : (
    <div className={classes} role={role} ref={innerRef} {...rest}>
      {children || (!isStep && `${intValue}/${intMax}`)}
    </div>
  );
}) as ProgressComponent; // Needed to tell Typescript this does not return ReactNode but acutally JSX.Element*/

export type ProgressStepProps = React.ComponentPropsWithoutRef<'div'> & {
  dot?: React.ReactNode | React.ComponentPropsWithoutRef<'div'>;
  label?: React.ReactNode; // Deprecated
};

const Step = forwardRef<HTMLDivElement, ProgressStepProps>(
  function ProgressStep(
    {
      ['aria-label']: ariaLabel,
      className,
      children,
      dot,
      label, // Deprecated
      ...rest
    },
    ref
  ) {
    const isClickable = !!rest.onClick || undefined; // Fallback to undefined so React does not render attributes unless true
    const dotProps = toReactProps<React.ComponentPropsWithoutRef<'div'>>(dot);
    const { children: dotChildren, ...dotRest } = dotProps;

    return (
      <div
        {...rest}
        role="listitem"
        className={clsx(styles.step, className)}
        ref={ref}
      >
        <div
          {...dotRest}
          className={clsx(styles.dot, dotRest.className)}
          aria-hidden="true"
        >
          {dotChildren || label}
        </div>
        {!!ariaLabel && <ScreenReaderOnly>{ariaLabel}</ScreenReaderOnly>}
        {/* Always render description element, so we can ensure we have role button and focus outline when clickable */}
        <div
          className={styles.description}
          onKeyDown={isClickable && onRoleButtonKeyDown}
          role={isClickable && 'button'}
          tabIndex={isClickable && 0}
        >
          {children}
        </div>
      </div>
    );
  }
);

export const Progress = Object.assign(ProgressComp, { Step });

// Helper to get boundingClientRect without CSS transforms, as Progress dots scale with CSS animation
const getRect = (el?: Element | null) => {
  const width = el?.clientWidth || 0;
  const height = el?.clientHeight || 0;
  const rect = { left: 0, top: 0, width, height };

  while (el instanceof HTMLElement) {
    rect.left += el.offsetLeft;
    rect.top += el.offsetTop;
    el = el.offsetParent as HTMLElement;
  }

  return { ...rect, right: width + rect.left, bottom: height + rect.top };
};
